import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import FilesystemCustomUtils from './filesystem.custom.utils';

export default class LogoService {
  private static LOGO_DIRECTORY = 'logo';
  private static LOGO_CACHE_KEY_PREFIX = 'logo_cache_';
  private static logoCache: Map<string, string> = new Map();
  private filesystemCustomUtils: FilesystemCustomUtils;

  constructor() {
    this.filesystemCustomUtils = new FilesystemCustomUtils();
  }

  /**
   * Get program logo URL/path
   * For mobile: checks cache first, downloads if missing
   * For web: creates blob URL from base64 (browser will cache)
   */
  async getProgramLogo(shortName: string | undefined, forceRefresh = false): Promise<string> {
    if (!shortName || shortName.trim() === '') {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      return require('@/theme/liga-logo-white.png');
    }

    const isMobile = Capacitor.isNativePlatform();
    const cacheKey = `${LogoService.LOGO_CACHE_KEY_PREFIX}${shortName}`;

    // For web platform, fetch base64 and create blob URL (browser will cache)
    if (!isMobile) {
      // Check in-memory cache first
      if (!forceRefresh && LogoService.logoCache.has(shortName)) {
        return LogoService.logoCache.get(shortName)!;
      }

      try {
        const tokenQuery = await Preferences.get({ key: "jwt" });
        const token = tokenQuery.value;
        
        if (token) {
          // Fetch base64 data and create blob URL
          const response = await CapacitorHttp.get({
            url: `${process.env.VUE_APP_API_EXT_URL}api/google/file/base64/name/${shortName}.png`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: 60000,
          });

          if (response.data) {
            // Convert base64 to blob URL
            const base64Data = response.data;
            const blob = this.base64ToBlob(base64Data, 'image/png');
            const blobUrl = URL.createObjectURL(blob);
            
            // Cache the blob URL
            LogoService.logoCache.set(shortName, blobUrl);
            return blobUrl;
          }
        } else {
          // No token available, use fallback
          console.warn('No authentication token available for logo, using fallback');
        }
      } catch (error) {
        console.warn(`Failed to load logo for ${shortName} from API, using fallback:`, error);
      }
      
      // Fallback to local resource
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const fallback = require(`@/theme/programs/${shortName}.png`);
        LogoService.logoCache.set(shortName, fallback);
        return fallback;
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const defaultLogo = require('@/theme/liga-logo-white.png');
        LogoService.logoCache.set(shortName, defaultLogo);
        return defaultLogo;
      }
    }

    // For mobile platform, use filesystem cache
    try {
      // Check in-memory cache first
      if (!forceRefresh && LogoService.logoCache.has(shortName)) {
        return LogoService.logoCache.get(shortName)!;
      }

      // Check if logo exists in filesystem
      const logoPath = `${LogoService.LOGO_DIRECTORY}/${shortName}.png`;
      
      try {
        const fileStat = await Filesystem.stat({
          path: logoPath,
          directory: Directory.Data,
        });

        if (fileStat && !forceRefresh) {
          // Logo exists in cache, get URI
          const fileUri = await Filesystem.getUri({
            path: logoPath,
            directory: Directory.Data,
          });
          
          const convertedUri = Capacitor.convertFileSrc(fileUri.uri);
          LogoService.logoCache.set(shortName, convertedUri);
          return convertedUri;
        }
      } catch (error) {
        // File doesn't exist, need to download
        console.log(`Logo not found in cache for ${shortName}, downloading...`);
      }

      // Download logo if not in cache or force refresh
      return await this.downloadAndCacheLogo(shortName);
    } catch (error) {
      console.error(`Error getting logo for ${shortName}:`, error);
      // Fallback to local resource
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return require(`@/theme/programs/${shortName}.png`);
      } catch (fallbackError) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return require('@/theme/liga-logo-white.png');
      }
    }
  }

  /**
   * Download logo from API and cache it
   */
  private async downloadAndCacheLogo(shortName: string): Promise<string> {
    try {
      const tokenQuery = await Preferences.get({ key: "jwt" });
      const token = tokenQuery.value;

      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await CapacitorHttp.get({
        url: `${process.env.VUE_APP_API_EXT_URL}api/google/file/base64/name/${shortName}.png`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });

      if (response.data) {
        // Store logo in filesystem
        const stored = await this.filesystemCustomUtils.storeFile(
          response.data,
          `${shortName}.png`,
          LogoService.LOGO_DIRECTORY
        );

        if (stored) {
          // Get URI for the stored file
          const fileUri = await Filesystem.getUri({
            path: `${LogoService.LOGO_DIRECTORY}/${shortName}.png`,
            directory: Directory.Data,
          });

          const convertedUri = Capacitor.convertFileSrc(fileUri.uri);
          LogoService.logoCache.set(shortName, convertedUri);
          return convertedUri;
        }
      }

      throw new Error('Failed to download logo');
    } catch (error) {
      console.error(`Failed to download logo for ${shortName}:`, error);
      throw error;
    }
  }

  /**
   * Download all program logos
   */
  async downloadAllProgramLogos(programs: Array<{ shortName: string }>): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      // For web, logos will be loaded on demand
      return;
    }

    const downloadPromises = programs.map(async (program) => {
      try {
        await this.getProgramLogo(program.shortName, false);
      } catch (error) {
        console.error(`Failed to download logo for ${program.shortName}:`, error);
      }
    });

    await Promise.allSettled(downloadPromises);
  }

  /**
   * Refresh a specific logo (force re-download)
   */
  async refreshLogo(shortName: string): Promise<string> {
    // Clear from cache
    const oldUrl = LogoService.logoCache.get(shortName);
    LogoService.logoCache.delete(shortName);
    
    // Revoke blob URL if it's a web blob
    if (oldUrl && oldUrl.startsWith('blob:')) {
      URL.revokeObjectURL(oldUrl);
    }
    
    // Try to delete from filesystem (mobile only)
    if (Capacitor.isNativePlatform()) {
      try {
        await Filesystem.deleteFile({
          path: `${LogoService.LOGO_DIRECTORY}/${shortName}.png`,
          directory: Directory.Data,
        });
      } catch (error) {
        // File might not exist, ignore
      }
    }

    // Download fresh logo
    return await this.getProgramLogo(shortName, true);
  }

  /**
   * Refresh all logos
   */
  async refreshAllLogos(programs: Array<{ shortName: string }>): Promise<void> {
    const refreshPromises = programs.map(async (program) => {
      try {
        await this.refreshLogo(program.shortName);
      } catch (error) {
        console.error(`Failed to refresh logo for ${program.shortName}:`, error);
      }
    });

    await Promise.allSettled(refreshPromises);
  }

  /**
   * Clear logo cache
   */
  async clearCache(): Promise<void> {
    // Revoke blob URLs for web
    if (!Capacitor.isNativePlatform()) {
      LogoService.logoCache.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    }
    
    LogoService.logoCache.clear();
    
    if (Capacitor.isNativePlatform()) {
      try {
        // Try to delete logo directory
        const files = await Filesystem.readdir({
          path: LogoService.LOGO_DIRECTORY,
          directory: Directory.Data,
        });

        for (const file of files.files) {
          try {
            await Filesystem.deleteFile({
              path: `${LogoService.LOGO_DIRECTORY}/${file}`,
              directory: Directory.Data,
            });
          } catch (error) {
            console.warn(`Failed to delete logo file ${file}:`, error);
          }
        }
      } catch (error) {
        // Directory might not exist, ignore
      }
    }
  }

  /**
   * Convert base64 string to Blob
   */
  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }
}

